import {CommentState} from 'atoms/comment';
import {COMMENT_EDITOR_KEY_ATTRIBUTE} from 'components/DSYS/editor/utils/commentEditorRoots';
import {LexicalEditor} from 'lexical';
import {useEffect, useRef} from 'react';

import {
  expandCollapsedSectionsContainingComment,
  findCommentPositionAnchorElement,
  getScrollTargetForElement,
  getScrollTargetMetrics,
  getScrollTargetViewportTop,
  scrollAttachedCommentToViewportRatio,
  type ScrollTarget,
} from './commentsHelpers';

const COMMENTS_PANEL_ACTIVE_COMMENT_VIEWPORT_RATIO = 1 / 3;
const COMMENT_SCROLL_LAYOUT_STABLE_FRAMES = 4;
const COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX = 1;
const COMMENT_SCROLL_ALIGNMENT_TOLERANCE_PX = 8;
const COMMENT_SCROLL_LAYOUT_STABLE_TIMEOUT_MS = 3000;
const INITIAL_URL_COMMENT_SCROLL_DELAY_MS = 250;

type AnchorLayoutSnapshot = {
  anchorScrollTop: number;
  scrollTarget: ScrollTarget;
  scrollHeight: number;
  clientHeight: number;
  maxScrollTop: number;
};

const isWindowScrollTarget = (scrollTarget: ScrollTarget): scrollTarget is Window =>
  scrollTarget === window;

export const usePlanningCommentScroll = ({
  activeComment,
  editorContainerEl,
  planningEditor,
  planningDocumentId,
  isDocumentSynced,
  isEditorStateReady,
}: {
  activeComment: CommentState | null;
  editorContainerEl: HTMLElement | null;
  planningEditor: LexicalEditor | null;
  planningDocumentId: string | undefined;
  isDocumentSynced: boolean;
  isEditorStateReady: boolean;
}) => {
  const completedCommentScrollsRef = useRef(new Set<string | number>());

  useEffect(() => {
    if (!activeComment?.threadId || activeComment.scrollIntoView === false) return;
    if (!editorContainerEl || !planningEditor || !isDocumentSynced || !isEditorStateReady) return;

    const threadId = activeComment.threadId;
    const originId = activeComment.planningCommentOriginId ?? undefined;
    const activationKey = activeComment.activationId ?? `legacy:${threadId}`;
    if (completedCommentScrollsRef.current.has(activationKey)) return;

    const commentRoot = editorContainerEl;
    const scheduledFrameIds = new Set<number>();
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;
    let settleDelayId: number | null = null;
    let isStopped = false;
    let isStableScrollScheduled = false;

    const scheduleFrame = (callback: FrameRequestCallback) => {
      const frameId = requestAnimationFrame((time) => {
        scheduledFrameIds.delete(frameId);
        if (isStopped) return;
        callback(time);
      });
      scheduledFrameIds.add(frameId);
    };

    const withActiveAnchorOptions = <T extends object>(
      options: T
    ): T & {editorKey?: string; originId?: string | null} => ({
      ...options,
      ...(activeComment.editorKey ? {editorKey: activeComment.editorKey} : {}),
      ...(originId ? {originId} : {}),
    });

    const scrollActiveCommentIntoView = (behavior: ScrollBehavior = 'smooth') =>
      scrollAttachedCommentToViewportRatio(
        threadId,
        withActiveAnchorOptions({
          behavior,
          viewportRatio: COMMENTS_PANEL_ACTIVE_COMMENT_VIEWPORT_RATIO,
        }),
        commentRoot
      );

    const stopObserving = () => {
      observer?.disconnect();
      observer = null;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const stopSettleDelay = () => {
      if (settleDelayId !== null) {
        window.clearTimeout(settleDelayId);
        settleDelayId = null;
      }
    };

    const stopWatching = () => {
      isStopped = true;
      stopObserving();
      stopSettleDelay();
    };

    const completeScroll = () => {
      completedCommentScrollsRef.current.add(activationKey);
      stopWatching();
    };

    const getVisibleAnchorElement = () => {
      const anchor = findCommentPositionAnchorElement(threadId, commentRoot, {
        includeHiddenFallback: false,
        ...(activeComment.editorKey ? {editorKey: activeComment.editorKey} : {}),
        ...(originId ? {originId} : {}),
      });
      if (!anchor) return null;

      const rect = anchor.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      return anchor;
    };

    const getScrollTargetViewportOriginTop = (scrollTarget: ScrollTarget) =>
      isWindowScrollTarget(scrollTarget) ? 0 : scrollTarget.getBoundingClientRect().top;

    const getAnchorLayoutSnapshot = (): AnchorLayoutSnapshot | null => {
      const anchor = getVisibleAnchorElement();
      if (!anchor) return null;

      const scrollTarget = getScrollTargetForElement(anchor, commentRoot);
      const metrics = getScrollTargetMetrics(scrollTarget);
      const anchorRect = anchor.getBoundingClientRect();

      return {
        anchorScrollTop:
          anchorRect.top - getScrollTargetViewportOriginTop(scrollTarget) + metrics.scrollTop,
        scrollTarget,
        scrollHeight: metrics.scrollHeight,
        clientHeight: metrics.clientHeight,
        maxScrollTop: metrics.maxScrollTop,
      };
    };

    const getAnchorAlignment = () => {
      const anchor = getVisibleAnchorElement();
      if (!anchor) return null;

      const scrollTarget = getScrollTargetForElement(anchor, commentRoot);
      const metrics = getScrollTargetMetrics(scrollTarget);
      const anchorRect = anchor.getBoundingClientRect();
      const anchorViewportPoint = anchorRect.top;
      const targetViewportPoint = getScrollTargetViewportTop(
        scrollTarget,
        COMMENTS_PANEL_ACTIVE_COMMENT_VIEWPORT_RATIO
      );
      const layout: AnchorLayoutSnapshot = {
        anchorScrollTop:
          anchorRect.top - getScrollTargetViewportOriginTop(scrollTarget) + metrics.scrollTop,
        scrollTarget,
        scrollHeight: metrics.scrollHeight,
        clientHeight: metrics.clientHeight,
        maxScrollTop: metrics.maxScrollTop,
      };

      return {
        delta: anchorViewportPoint - targetViewportPoint,
        metrics,
        layout,
      };
    };

    const isLayoutSnapshotStable = (
      previous: AnchorLayoutSnapshot | null,
      next: AnchorLayoutSnapshot
    ) => {
      if (!previous || previous.scrollTarget !== next.scrollTarget) return false;

      return (
        Math.abs(next.anchorScrollTop - previous.anchorScrollTop) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX &&
        Math.abs(next.scrollHeight - previous.scrollHeight) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX &&
        Math.abs(next.clientHeight - previous.clientHeight) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX &&
        Math.abs(next.maxScrollTop - previous.maxScrollTop) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX
      );
    };

    const isAnchorPositionSnapshotStable = (
      previous: AnchorLayoutSnapshot | null,
      next: AnchorLayoutSnapshot
    ) => {
      if (!previous || previous.scrollTarget !== next.scrollTarget) return false;

      return (
        Math.abs(next.anchorScrollTop - previous.anchorScrollTop) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX &&
        Math.abs(next.clientHeight - previous.clientHeight) <=
          COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX
      );
    };

    const canScrollTowardAlignment = ({
      delta,
      metrics,
    }: NonNullable<ReturnType<typeof getAnchorAlignment>>) => {
      if (delta > COMMENT_SCROLL_ALIGNMENT_TOLERANCE_PX) {
        return metrics.scrollTop < metrics.maxScrollTop - COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX;
      }

      if (delta < -COMMENT_SCROLL_ALIGNMENT_TOLERANCE_PX) {
        return metrics.scrollTop > COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX;
      }

      return false;
    };

    const scheduleScrollWhenLayoutSettles = () => {
      if (isStableScrollScheduled) return;
      isStableScrollScheduled = true;

      const initialDelayMs =
        activeComment.source === 'url' ? INITIAL_URL_COMMENT_SCROLL_DELAY_MS : 0;

      const waitForStableAnchor = () => {
        let stableFrames = 0;
        let previousLayout: AnchorLayoutSnapshot | null = null;
        const startedAt = performance.now();

        const verifyScrollAlignment = (initialScrollLayout: AnchorLayoutSnapshot) => {
          const verifyStartedAt = performance.now();
          let layoutAtLastAutoScroll = initialScrollLayout;
          let previousScrollTop: number | null = null;
          let stableScrollFrames = 0;

          const verify = (now: DOMHighResTimeStamp) => {
            if (now - verifyStartedAt > COMMENT_SCROLL_LAYOUT_STABLE_TIMEOUT_MS) {
              completeScroll();
              return;
            }

            const alignment = getAnchorAlignment();
            if (!alignment) {
              stableScrollFrames = 0;
              previousScrollTop = null;
              scheduleFrame(verify);
              return;
            }

            if (
              previousScrollTop !== null &&
              Math.abs(alignment.metrics.scrollTop - previousScrollTop) <=
                COMMENT_SCROLL_LAYOUT_STABLE_TOLERANCE_PX
            ) {
              stableScrollFrames += 1;
            } else {
              stableScrollFrames = 0;
            }
            previousScrollTop = alignment.metrics.scrollTop;

            if (stableScrollFrames >= COMMENT_SCROLL_LAYOUT_STABLE_FRAMES) {
              if (Math.abs(alignment.delta) <= COMMENT_SCROLL_ALIGNMENT_TOLERANCE_PX) {
                completeScroll();
                return;
              }

              const layoutChangedSinceLastAutoScroll = !isLayoutSnapshotStable(
                layoutAtLastAutoScroll,
                alignment.layout
              );

              // Only re-scroll when the anchor/table layout changed after our last auto-scroll.
              // A stable layout with a different scrollTop means the viewport moved, so chasing
              // the old alignment would pin the page to the active comment.
              if (layoutChangedSinceLastAutoScroll && canScrollTowardAlignment(alignment)) {
                layoutAtLastAutoScroll = alignment.layout;
                scrollActiveCommentIntoView('auto');
                stableScrollFrames = 0;
                previousScrollTop = null;
                scheduleFrame(verify);
                return;
              }

              completeScroll();
              return;
            }

            scheduleFrame(verify);
          };

          scheduleFrame(verify);
        };

        const measure = (now: DOMHighResTimeStamp) => {
          // Lexical/Yjs can temporarily replace or hide comment anchors while the document settles.
          // If the anchor never becomes visible or never reaches a stable position, cap the rAF loop
          // so one failed comment open cannot keep scheduling work forever.
          if (now - startedAt > COMMENT_SCROLL_LAYOUT_STABLE_TIMEOUT_MS) {
            if (getVisibleAnchorElement() && scrollActiveCommentIntoView()) {
              completeScroll();
            } else {
              stopWatching();
            }
            return;
          }

          const nextLayout = getAnchorLayoutSnapshot();
          if (nextLayout === null) {
            stableFrames = 0;
            previousLayout = null;
            scheduleFrame(measure);
            return;
          }

          const isLayoutStable = isLayoutSnapshotStable(previousLayout, nextLayout);
          // URL opens already wait for the initial document-load delay. After that,
          // dense right-rail layout can keep scrollHeight changing even when the target
          // anchor's document position is ready, so allow the initial URL scroll to start
          // once the anchor position is stable. Other open sources keep the stricter full
          // layout check because they do not have that load-delay buffer.
          const isStableEnoughForInitialUrlScroll =
            activeComment.source === 'url' &&
            isAnchorPositionSnapshotStable(previousLayout, nextLayout);

          if (isLayoutStable || isStableEnoughForInitialUrlScroll) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
          }
          previousLayout = nextLayout;

          if (stableFrames < COMMENT_SCROLL_LAYOUT_STABLE_FRAMES) {
            scheduleFrame(measure);
            return;
          }

          const didScroll = scrollActiveCommentIntoView();
          if (didScroll) {
            verifyScrollAlignment(nextLayout);
          }
        };

        scheduleFrame(measure);
      };

      if (initialDelayMs > 0) {
        settleDelayId = window.setTimeout(() => {
          settleDelayId = null;
          waitForStableAnchor();
        }, initialDelayMs);
      } else {
        waitForStableAnchor();
      }
    };

    const tryFocusComment = () => {
      const expanded = expandCollapsedSectionsContainingComment({
        threadId,
        editor: planningEditor,
        root: commentRoot,
        collabId: planningDocumentId,
        editorKey: activeComment.editorKey,
        originId,
      });

      if (expanded) {
        scheduleScrollWhenLayoutSettles();
        return true;
      }

      if (
        findCommentPositionAnchorElement(threadId, commentRoot, {
          includeHiddenFallback: false,
          ...(activeComment.editorKey ? {editorKey: activeComment.editorKey} : {}),
          originId,
        })
      ) {
        scheduleScrollWhenLayoutSettles();
        return true;
      }

      return false;
    };

    scheduleFrame(() => {
      if (tryFocusComment()) return;

      observer = new MutationObserver(() => {
        if (tryFocusComment()) {
          stopObserving();
        }
      });
      observer.observe(editorContainerEl, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'aria-hidden',
          'data-collapsed',
          'data-highlight-id',
          COMMENT_EDITOR_KEY_ATTRIBUTE,
        ],
      });
      timeoutId = window.setTimeout(stopWatching, 3000);
    });

    return () => {
      stopWatching();
      scheduledFrameIds.forEach((frameId) => cancelAnimationFrame(frameId));
    };
  }, [
    activeComment?.activationId,
    activeComment?.planningCommentOriginId,
    activeComment?.scrollIntoView,
    activeComment?.source,
    activeComment?.threadId,
    activeComment?.editorKey,
    editorContainerEl,
    isDocumentSynced,
    isEditorStateReady,
    planningDocumentId,
    planningEditor,
  ]);
};
